<script lang="ts" setup>
import { OtModuleContainer } from '@1trading/design-system';
import { debounce } from 'lodash/fp';
import ResizeObserver from 'resize-observer-polyfill';
import { computed, toRefs, ref, onMounted, onBeforeUnmount, watch } from 'vue';

import { marketService } from '@exchange/libs/market/service/src';
import { useOrderbook, useOrderbookAggregation } from '@exchange/libs/orderbook/service/src';

import { ORDERBOOK_RESIZE_DEBOUNCE_MS } from './constants.config';
import OrderbookComponent from './Orderbook.vue';

const props = defineProps({
  marketId: { type: String, required: true },
  mini: { type: Boolean, default: false },
  showTitle: { type: Boolean, default: true },
});

const { marketId } = toRefs(props);

const { getMarket, getMarketPrecision, getMarketLastPrice } = marketService;

const { orderbook, orderbookIsLoading, orderbookAddedTime, msPerFrame, hasAnimations, orderbookResubscribe } = useOrderbook(marketId);
const { aggregationLevel, aggregationRaisePossible, aggregationReducePossible, aggregationExecute } = useOrderbookAggregation(orderbook);

const market = computed(() => getMarket(marketId.value));
const marketLastPrice = computed(() => getMarketLastPrice(market.value));
const marketPrecisions = computed(() => getMarketPrecision(market.value));

const rootComponent = ref<InstanceType<typeof OtModuleContainer>>();
const orderbookComponent = ref<InstanceType<typeof OrderbookComponent>>();

let resizeObserver: ResizeObserver;
let onResize: (rect: DOMRectReadOnly) => void;

const recenterOrderbook = () => {
  orderbookComponent.value?.recenter();
};

const startObserving = () => {
  if (rootComponent.value?.$el && resizeObserver) {
    resizeObserver.observe(rootComponent.value.$el);
  }
};

watch(rootComponent, startObserving);

/*
 * We're already using a resize observer in the Orderbook component, but now that we've disabled scroll
 * on the container, we need to trigger a recenter when the container's height changes to ensure the orderbook
 * is always centered (as the orderbook itself does not change its height, it overflows the container).
 *
 * Really, the whole orderbook needs rewriting as it was never intended to be used with a fixed height container.
 */
onMounted(() => {
  onResize = debounce(ORDERBOOK_RESIZE_DEBOUNCE_MS, () => {
    recenterOrderbook();
  });

  resizeObserver = new ResizeObserver((resizeEvents: Array<ResizeObserverEntry>) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const event of resizeEvents) {
      if (onResize) {
        onResize(event.contentRect);
      }
    }
  });

  startObserving();
});

onBeforeUnmount(() => {
  if (resizeObserver) {
    resizeObserver.disconnect();
  }
});
</script>

<template>
  <ot-module-container
    id="orderbook-container"
    ref="rootComponent"
    class="orderbook-container"
    scroll-container-class="orderbook-container__scroll-container"
    :class="{ 'orderbook-container--mini': mini }"
    :loading="orderbookIsLoading || !orderbook || !market"
    :scroll-settings="{ startAtMiddle: true }"
    :title="showTitle ? $t('modules.orderbook.titles.orderbook') : ''"
  >
    <template #main-content="{ doScrollAtMiddle }">
      <orderbook-component
        v-if="orderbook && market"
        ref="orderbookComponent"
        :aggregation-execute="aggregationExecute"
        :aggregation-level="aggregationLevel"
        :aggregation-raise-possible="aggregationRaisePossible"
        :aggregation-reduce-possible="aggregationReducePossible"
        :amount-precision="marketPrecisions.amount"
        :do-scroll-at-middle="doScrollAtMiddle"
        :has-animations="hasAnimations"
        :last-price="marketLastPrice"
        :market-id="marketId"
        :market-precision="marketPrecisions.market"
        :mini="mini"
        :ms-per-frame="msPerFrame"
        :orderbook-added-time="orderbookAddedTime"
        :orderbook-resubscribe="orderbookResubscribe"
        :orderbook="orderbook"
        :total-precision="marketPrecisions.total"
        :use-fast-orderbook="true"
      />
    </template>
  </ot-module-container>
</template>

<style lang="scss">
.orderbook-container {
  padding-bottom: 7px;
  border: 0;

  &__scroll-container {
    border-bottom-left-radius: 12px 3px;
    border-bottom-right-radius: 12px 3px;
    overflow: hidden;
  }
}

.orderbook-container--mini {
  padding-top: 7px;
}
</style>
